import React, { useEffect, useState } from 'react'
import { Card, Dropdown, Form } from 'react-bootstrap'
import { Table, Tbody, Td, Th, Thead, Tr } from 'react-super-responsive-table'
import 'react-super-responsive-table/dist/SuperResponsiveTableStyle.css'
import {
  BsArrowClockwise,
  BsFunnelFill,
  BsPencilFill,
  BsSearch,
  BsThreeDotsVertical,
} from 'react-icons/bs'
import { FileEarmarkText } from 'react-bootstrap-icons'
import { LuGraduationCap } from 'react-icons/lu'
import { useNavigate } from 'react-router-dom'
import Cookies from 'js-cookie'
import axios from 'axios'
import toast from 'react-hot-toast'
import { useAuth } from './AuthContext'
import { useSidebar } from './SidebarContext'
import ProfileDropdown from './commonComponents/ProfileDropdown'
import { useInstitute } from './commonComponents/InstituteContext'
import TablePagination from './commonComponents/TablePagination'
import { BASE_URL } from './commonComponents/ApiEndpoints'

const formatTemplateType = (type) => {
  if (!type) return '-'
  return String(type)
}

export default function ManageTemplates() {
  const { logout, user } = useAuth()
  const { isCollapsed } = useSidebar()
  const { logo } = useInstitute()
  const navigate = useNavigate()
  const [search, setSearch] = useState('')
  const [itemsPerPage, setItemsPerPage] = useState(10)
  const [currentPage, setCurrentPage] = useState(1)
  const [showFilterCard, setShowFilterCard] = useState(false)
  const [templateList, setTemplateList] = useState([])
  const [loadingTemplates, setLoadingTemplates] = useState(false)
  const [marginLeft, setMarginLeft] = useState('0px')
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' ? window.innerWidth <= 768 : false,
  )
  const [logoError, setLogoError] = useState(false)

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 992) {
        const expandedWidth = 245
        const collapsedWidth = 85
        const gap = 1

        setMarginLeft(
          isCollapsed
            ? `${collapsedWidth + gap}px`
            : `${expandedWidth + gap}px`,
        )
      } else {
        setMarginLeft('0px')
      }

      setIsMobile(window.innerWidth <= 768)
    }

    handleResize()
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [isCollapsed])

  const getAuthToken = () => {
    if (user?.token) return user.token

    try {
      const userCookie = Cookies.get('user')
      return userCookie ? JSON.parse(userCookie)?.token : ''
    } catch {
      return ''
    }
  }

  const fetchTemplates = async () => {
    const instituteId = Number(Cookies.get('institute_id'))
    const token = getAuthToken()

    if (!Number.isFinite(instituteId) || instituteId <= 0) {
      toast.error('Institute ID not found. Please select institute again.')
      setTemplateList([])
      return
    }

    if (!token) {
      toast.error('User session not found. Please login again.')
      setTemplateList([])
      return
    }

    try {
      setLoadingTemplates(true)
      const response = await axios.get(`${BASE_URL}/institute-templates`, {
        headers: {
          Accept: 'application/json',
          Authorization: `Bearer ${token}`,
          'Institute-Id': instituteId,
        },
      })

      const apiTemplates = Array.isArray(response.data?.data)
        ? response.data.data
        : []

      setTemplateList(apiTemplates)
    } catch (error) {
      setTemplateList([])
      toast.error(error.response?.data?.message || 'Failed to fetch templates')
    } finally {
      setLoadingTemplates(false)
    }
  }

  useEffect(() => {
    fetchTemplates()
  }, [])

  useEffect(() => {
    setCurrentPage(1)
  }, [search, itemsPerPage])

  const searchedTemplates = templateList.filter((template) =>
    Object.values(template).some(
      (value) =>
        value !== null &&
        value !== undefined &&
        value.toString().toLowerCase().includes(search.trim().toLowerCase()),
    ),
  )

  const totalPages = Math.ceil(searchedTemplates.length / itemsPerPage) || 0
  useEffect(() => {
    if (currentPage > totalPages && totalPages !== 0) {
      setCurrentPage(1)
    }
  }, [searchedTemplates.length, totalPages, currentPage])

  const indexOfLastItem = currentPage * itemsPerPage
  const indexOfFirstItem = indexOfLastItem - itemsPerPage
  const filteredTemplates = searchedTemplates.slice(
    indexOfFirstItem,
    indexOfLastItem,
  )

  const tableHeaderStyle = {
    fontSize: '13px',
    fontWeight: '600',
    padding: '16px',
    border: 'none',
    borderBottom: '1px solid #D1D3D4',
    textAlign: 'left',
    color: '#111827',
    fontFamily: 'Inter, system-ui, sans-serif',
  }

  const tableCellStyle = {
    fontSize: '14px',
    padding: '16px',
    border: 'none',
    color: '#111827',
    textAlign: 'left',
    fontFamily: 'Inter, system-ui, sans-serif',
  }

  const handleRefresh = () => {
    setSearch('')
    setCurrentPage(1)
    fetchTemplates()
  }

  return (
    <>
      <style>
        {`
          .dropdown-toggle-no-caret::after {
            display: none !important;
          }
          .table td {
            user-select: text;
            overflow: visible !important;
          }
          .dropdown-menu {
            z-index: 9999 !important;
          }
        `}
      </style>
      <div
        style={{
          marginLeft,
          transition: 'margin-left 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
          willChange: 'margin-left',
          WebkitTransition: 'margin-left 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
          position: 'relative',
          zIndex: 1,
          marginTop: '0px',
          paddingLeft: isCollapsed ? '12px' : '12px',
          paddingRight: isCollapsed ? '12px' : '12px',
        }}
      >
        <div
          className="container-fluid p-0"
          style={{
            marginTop: typeof window !== 'undefined' && window.innerWidth < 992 ? '55px' : '-12px',
            paddingLeft: '0px',
            paddingRight: '0px',
          }}
        >
          <div style={{ marginLeft: isMobile ? '0px' : '-40px' }}>
            <div
              className="d-flex justify-content-between align-items-center"
              style={{
                background: '#F3F4F6',
                paddingLeft: isMobile ? '16px' : '28px',
                paddingRight: isMobile ? '16px' : '28px',
                paddingTop: '12px',
                paddingBottom: '12px',
                overflow: 'visible',
                position: 'relative',
                zIndex: 1050,
                borderBottom: '1px solid #D1D3D4',
                width: '100%',
                maxWidth: '100%',
                boxSizing: 'border-box',
              }}
            >
              <div className="d-flex align-items-center gap-3">
                <div
                  style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '50%',
                    overflow: 'hidden',
                    background: '#223843',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  {!logoError && logo ? (
                    <img
                      src={logo}
                      alt="School Logo"
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      onError={() => setLogoError(true)}
                    />
                  ) : (
                    <LuGraduationCap size={20} style={{ color: '#ffffff' }} />
                  )}
                </div>

                <h3
                  className="m-0 fw-bold"
                  style={{
                    letterSpacing: '-0.02em',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    fontSize: 'clamp(18px, 2.5vw, 22px)',
                    color: '#223843',
                    lineHeight: '1.2',
                    fontFamily: 'Inter, system-ui, sans-serif',
                  }}
                >
                  Templates
                </h3>
              </div>

              {!isMobile && <ProfileDropdown logout={logout} />}
            </div>
          </div>
        </div>

        <div
          className="container-fluid p-0 mt-4"
          style={{
            minHeight: '100vh',
            paddingLeft: isMobile ? '12px' : isCollapsed ? '32px' : '40px',
            paddingRight: isMobile ? '12px' : '32px',
            paddingTop: '32px',
            paddingBottom: '32px',
            maxWidth: '100%',
            overflowX: isMobile ? 'hidden' : 'visible',
            boxSizing: 'border-box',
          }}
        >
          <div
            className="d-flex justify-content-between align-items-center mb-4"
            style={{
              gap: '12px',
              paddingRight: isMobile ? '0px' : '0',
              boxSizing: 'border-box',
            }}
          >
            {/* <div className="d-flex align-items-center gap-3" style={{ minWidth: 0, flexWrap: isMobile ? 'wrap' : 'nowrap' }}>
              <div
                className="d-flex align-items-center"
                style={{
                  backgroundColor: '#ffffff',
                  padding: isMobile ? '8px 10px' : '8px 16px',
                  borderRadius: '8px',
                  gap: '8px',
                  border: '1px solid #D1D3D4',
                  maxWidth: '100%',
                  boxSizing: 'border-box',
                }}
              >
                <span style={{ color: '#6b7280', fontSize: '13px', whiteSpace: 'nowrap', fontFamily: 'Inter, system-ui, sans-serif' }}>
                  Show:
                </span>
                <Form.Select
                  className="w-auto"
                  value={itemsPerPage}
                  onChange={(e) => {
                    setItemsPerPage(parseInt(e.target.value, 10))
                    setCurrentPage(1)
                  }}
                  style={{
                    borderRadius: '6px',
                    border: '1px solid #D1D3D4',
                    backgroundColor: '#ffffff',
                    color: '#111827',
                    fontSize: '13px',
                    fontWeight: '500',
                    padding: '4px 32px 4px 12px',
                    minWidth: isMobile ? '120px' : '140px',
                    maxWidth: '100%',
                    cursor: 'pointer',
                    height: '36px',
                    fontFamily: 'Inter, system-ui, sans-serif',
                  }}
                >
                  <option value="10">10 per page</option>
                  <option value="20">20 per page</option>
                  <option value="30">30 per page</option>
                </Form.Select>
              </div>
              <button
                onClick={() => setShowFilterCard(!showFilterCard)}
                style={{
                  width: '48px',
                  height: '48px',
                  borderRadius: '50%',
                  backgroundColor: showFilterCard ? '#223843' : '#ffffff',
                  border: '1px solid #D1D3D4',
                  color: showFilterCard ? '#ffffff' : '#223843',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#223843'
                  e.currentTarget.style.color = '#ffffff'
                  e.currentTarget.style.borderColor = '#223843'
                  e.currentTarget.style.transform = 'scale(1.05)'
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = showFilterCard ? '#223843' : '#ffffff'
                  e.currentTarget.style.color = showFilterCard ? '#ffffff' : '#223843'
                  e.currentTarget.style.borderColor = '#D1D3D4'
                  e.currentTarget.style.transform = 'scale(1)'
                }}
                title={showFilterCard ? 'Hide Filters' : 'Show Filters'}
              >
                <BsFunnelFill size={20} />
              </button>
            </div> */}
          </div>

          <div
            style={{
              maxHeight: showFilterCard ? '1000px' : '0',
              overflow: showFilterCard ? 'visible' : 'hidden',
              transition: 'max-height 0.3s ease-in-out',
              marginBottom: showFilterCard ? '24px' : '0',
            }}
          >
            {showFilterCard && (
              <Card
                style={{
                  border: '1px solid #D1D3D4',
                  borderRadius: '12px',
                  backgroundColor: '#ffffff',
                  overflow: 'visible',
                }}
              >
                <div
                  style={{
                    background: '#ffffff',
                    padding: '20px',
                    borderBottom: '1px solid #F3F4F6',
                  }}
                >
                  <div className="d-flex justify-content-between align-items-center gap-3">
                    <div className="d-flex align-items-center gap-3">
                      <div
                        style={{
                          width: '40px',
                          height: '40px',
                          borderRadius: '50%',
                          backgroundColor: '#223843',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <BsSearch style={{ color: '#ffffff', fontSize: '18px' }} />
                      </div>
                      <div>
                        <h5 className="m-0 fw-semibold text-start" style={{ color: '#111827', fontSize: '16px', fontFamily: 'Inter, system-ui, sans-serif' }}>
                          Search Templates
                        </h5>
                        <p className="m-0 mt-1" style={{ fontSize: '13px', color: '#6b7280' }}>
                          Find templates by type or status
                        </p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={handleRefresh}
                      disabled={loadingTemplates}
                      style={{
                        width: '48px',
                        height: '48px',
                        borderRadius: '50%',
                        backgroundColor: loadingTemplates ? '#9ca3af' : '#223843',
                        border: 'none',
                        color: '#ffffff',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: loadingTemplates ? 'not-allowed' : 'pointer',
                        transition: 'all 0.2s ease',
                        flexShrink: 0,
                      }}
                      title="Refresh"
                    >
                      <BsArrowClockwise size={20} />
                    </button>
                  </div>
                </div>
                <Card.Body className="p-4" style={{ backgroundColor: '#ffffff' }}>
                  <div className="row g-3">
                    <div className="col-12 col-md-6">
                      <div style={{ position: 'relative' }}>
                        <label className="fw-semibold text-start mb-2 d-block" style={{ color: '#6b7280', fontSize: '13px', fontFamily: 'Inter, system-ui, sans-serif' }}>
                          Search Template
                        </label>
                        <div style={{ position: 'relative' }}>
                          <BsSearch
                            style={{
                              position: 'absolute',
                              left: '12px',
                              top: '50%',
                              transform: 'translateY(-50%)',
                              color: '#6b7280',
                              fontSize: '16px',
                              zIndex: 1,
                            }}
                          />
                          <Form.Control
                            type="text"
                            placeholder="Search by template type, status..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            style={{
                              paddingLeft: '40px',
                              borderRadius: '8px',
                              border: '1px solid #D1D3D4',
                              fontSize: '14px',
                              height: '40px',
                              backgroundColor: '#ffffff',
                              fontFamily: 'Inter, system-ui, sans-serif',
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            )}
          </div>

          <Card className="table-card" style={{ border: '1px solid #D1D3D4', borderRadius: '12px', overflow: isMobile ? 'hidden' : 'visible', backgroundColor: '#ffffff', position: 'relative', maxWidth: '100%' }}>
            <div className="table-wrapper" style={{ overflowX: 'auto', overflowY: 'visible', position: 'relative', borderRadius: '12px' }}>
              <Table className="table mb-0" style={{ borderRadius: '12px', overflow: 'visible', position: 'relative', marginBottom: '0' }}>
                <Thead>
                  <Tr style={{ backgroundColor: '#F3F4F6' }}>
                    {/* <Th style={tableHeaderStyle}>ID</Th> */}
                    <Th style={tableHeaderStyle}>Template Type</Th>
                    <Th style={tableHeaderStyle}>Selected</Th>
                    <Th style={{ ...tableHeaderStyle, textAlign: 'center' }}>Action</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {filteredTemplates.length > 0 ? (
                    filteredTemplates.map((template, index) => (
                      <Tr
                        key={template.id}
                        style={{
                          backgroundColor: index % 2 === 0 ? '#ffffff' : 'rgba(243, 244, 246, 0.4)',
                          borderBottom: '1px solid #E5E7EB',
                        }}
                      >
                        {/* <Td style={tableCellStyle}>{template.id}</Td> */}
                        <Td style={tableCellStyle}>
                          <div className="d-flex align-items-center gap-2">
                            <FileEarmarkText size={16} style={{ color: '#6b7280' }} />
                            {formatTemplateType(template.template_type)}
                          </div>
                        </Td>
                        <Td style={tableCellStyle}>
                          <span
                            style={{
                              backgroundColor: Number(template.selected) === 1 ? '#DCFCE7' : '#F3F4F6',
                              color: Number(template.selected) === 1 ? '#166534' : '#6b7280',
                              padding: '4px 10px',
                              borderRadius: '999px',
                              fontSize: '12px',
                              fontWeight: '600',
                            }}
                          >
                            {Number(template.selected) === 1 ? 'Active' : 'Inactive'}
                          </span>
                        </Td>
                        <Td className="text-center" style={{ padding: '16px', border: 'none' }}>
                          <Dropdown align="end">
                            <Dropdown.Toggle
                              variant="light"
                              id={`template-action-${template.id}`}
                              className="dropdown-toggle-no-caret"
                              style={{
                                width: '36px',
                                height: '36px',
                                borderRadius: '50%',
                                backgroundColor: 'transparent',
                                border: '1px solid #D1D3D4',
                                padding: '0',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                              }}
                            >
                              <BsThreeDotsVertical />
                            </Dropdown.Toggle>
                            <Dropdown.Menu
                              renderOnMount
                              popperConfig={{
                                strategy: 'fixed',
                                modifiers: [
                                  {
                                    name: 'offset',
                                    options: {
                                      offset: [0, 8],
                                    },
                                  },
                                  {
                                    name: 'flip',
                                    options: {
                                      fallbackPlacements: ['top-end', 'bottom-end', 'left-start'],
                                    },
                                  },
                                  {
                                    name: 'preventOverflow',
                                    options: {
                                      boundary: 'viewport',
                                      padding: 8,
                                    },
                                  },
                                ],
                              }}
                              style={{
                                borderRadius: '8px',
                                border: '1px solid #D1D3D4',
                                padding: '8px',
                                minWidth: '160px',
                              }}
                            >
                              <Dropdown.Item
                                onClick={() =>
                                  navigate(`/EditTemplate/${template.id}`, {
                                    state: { template },
                                  })
                                }
                                style={{ fontSize: '13px', padding: '10px 12px', display: 'flex', alignItems: 'center', gap: '10px' }}
                              >
                                <BsPencilFill size={18} /> Edit Template
                              </Dropdown.Item>
                            </Dropdown.Menu>
                          </Dropdown>
                        </Td>
                      </Tr>
                    ))
                  ) : (
                    <Tr>
                      <Td colSpan={4} style={{ ...tableCellStyle, textAlign: 'center', color: '#6b7280' }}>
                        {loadingTemplates ? 'Loading templates...' : 'No templates found.'}
                      </Td>
                    </Tr>
                  )}
                </Tbody>
              </Table>
            </div>
          </Card>

          {/* <TablePagination
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            totalPages={totalPages}
            indexOfFirstItem={indexOfFirstItem}
            indexOfLastItem={indexOfLastItem}
            totalItems={searchedTemplates.length}
            itemLabel="templates"
            maxVisiblePages={isMobile ? 5 : 7}
            paginationScrollable
          /> */}
        </div>
      </div>
    </>
  )
}
